import { getApiBase } from './apiBase';

const API_BASE = getApiBase();

function formatErrorDetail(error) {
  if (!error || error.detail == null) return null;
  const d = error.detail;
  if (typeof d === 'string') return d;
  if (typeof d === 'object' && d !== null && !Array.isArray(d) && typeof d.message === 'string') {
    return d.message;
  }
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join('; ');
  }
  return 'Request failed';
}

function makeHttpError(errorJson, response, endpoint) {
  const d = errorJson?.detail;
  const base = formatErrorDetail(errorJson) || fallbackHttpError(response, endpoint);
  const err = new Error(base);
  if (typeof d === 'object' && d !== null && d.code) {
    err.code = d.code;
  }
  return err;
}

function fallbackHttpError(response, endpoint) {
  const status = response?.status ?? 'unknown';
  const statusText = response?.statusText || 'Error';
  return `Request failed (${status} ${statusText}) for ${endpoint}`;
}

/**
 * Only dashboard shell routes get a hard redirect on 401. Public routes (login, legal, platform,
 * trailing slashes, future marketing pages) must never be forced back to /login or legal links
 * appear to "refresh" / do nothing.
 */
const PROTECTED_PILOT_PATH_PREFIXES = [
  '/dashboard',
  '/operations',
  '/devguard',
  '/critical-infrastructure',
  '/risk',
  '/assets',
  '/network-scan',
  '/compliance',
  '/verification-loop',
  '/autonomous-pipeline',
  '/fusion/cyber-range',
  '/runs',
  '/graph-findings',
  '/registry',
  '/remediation',
  '/forecasts',
  '/reports',
  '/resimulation',
  '/events',
];

function normalizePathnameFor401Redirect() {
  if (typeof window === 'undefined') return '';
  let p = window.location.pathname || '/';
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p.toLowerCase();
}

function isProtectedPilotShellPath(pathnameNorm) {
  const p = pathnameNorm || '';
  return PROTECTED_PILOT_PATH_PREFIXES.some((pre) => p === pre || p.startsWith(`${pre}/`));
}

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('aerta_token');
    this._refreshPromise = null;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('aerta_token', token);
    } else {
      localStorage.removeItem('aerta_token');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async refreshAccessToken() {
    if (this._refreshPromise) return this._refreshPromise;
    const bearer = this.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('aerta_token') : null);
    if (!bearer) {
      throw new Error('No session token to refresh');
    }
    this._refreshPromise = (async () => {
      const url = `${API_BASE}/api/auth/refresh`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
        },
        credentials: 'include',
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw makeHttpError(error, res, '/api/auth/refresh');
      }
      const data = await res.json().catch(() => ({}));
      const tok = data?.access_token;
      if (tok) this.setToken(tok);
      return tok;
    })();
    try {
      return await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const { timeoutMs, ...rest } = options;
    const attemptedRefresh = Boolean(options && options.__attemptedRefresh);
    let abortTimer;
    const fetchOpts = { ...rest };
    if (timeoutMs != null && Number.isFinite(Number(timeoutMs))) {
      const ctrl = new AbortController();
      abortTimer = setTimeout(() => ctrl.abort(), Number(timeoutMs));
      fetchOpts.signal = ctrl.signal;
    }
    const config = {
      ...fetchOpts,
      credentials: fetchOpts.credentials || 'include',
      headers: {
        ...this.getHeaders(),
        ...fetchOpts.headers,
      },
    };

    let response;
    try {
      response = await fetch(url, config);
    } catch (err) {
      if (abortTimer) clearTimeout(abortTimer);
      if (err && err.name === 'AbortError') {
        throw new Error(
          `Request timed out after ${timeoutMs}ms (${endpoint}). Check the API is reachable and not blocked.`
        );
      }
      const msg = err && err.message ? String(err.message) : String(err);
      const prod = process.env.NODE_ENV === 'production';
      const hint =
        API_BASE === ''
          ? prod
            ? ' Set REACT_APP_BACKEND_URL to your public API origin when building the pilot app (e.g. your Render service URL).'
            : ' Is the Docker API up on port 8002 (or set REACT_APP_PROXY_TARGET)? Dev proxy defaults to 8002 when API_BASE is empty.'
          : ` Check REACT_APP_BACKEND_URL (${API_BASE}) and that the API is reachable.`;
      if (msg === 'Failed to fetch' || msg.includes('NetworkError')) {
        throw new Error(`Failed to fetch.${hint}`);
      }
      throw err;
    }
    if (abortTimer) clearTimeout(abortTimer);

    if (response.status === 401) {
      const error = await response.json().catch(() => ({}));
      const message = formatErrorDetail(error) || fallbackHttpError(response, endpoint);
      const isLoginAttempt = endpoint.includes('/api/auth/login');
      const isRefreshAttempt = endpoint.includes('/api/auth/refresh');

      // If we have a refresh cookie, try silent refresh once, then retry the original request.
      if (!attemptedRefresh && !isLoginAttempt && !isRefreshAttempt) {
        try {
          await this.refreshAccessToken();
          return await this.request(endpoint, { ...options, __attemptedRefresh: true });
        } catch {
          // fall through to dispatch event + throw
        }
      }

      if (!isLoginAttempt && !isRefreshAttempt) {
        // Do NOT clear the token automatically.
        // Long-running agent/tool runs can trigger background 401s and auto-clearing
        // forces the whole app into a logged-out state even though the user may want
        // to re-auth in a new tab and keep context visible.
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('aerta:auth-expired', {
                detail: {
                  endpoint,
                  message,
                  protectedPath: isProtectedPilotShellPath(normalizePathnameFor401Redirect()),
                },
              }),
            );
          }
        } catch {
          /* ignore */
        }
      }
      throw makeHttpError(error, response, endpoint);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw makeHttpError(error, response, endpoint);
    }

    return response.json();
  }

  get(endpoint, requestOptions = {}) {
    return this.request(endpoint, { method: 'GET', ...requestOptions });
  }

  post(endpoint, data, requestOptions = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...requestOptions,
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
